import { useEffect } from "react";
import { CLIENT_BASE_PATH } from "../../environment";
import { BlogPost } from "./blogService";

const setMeta = (selector: string, attribute: string, value: string) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!element) {
    element = selector.startsWith("link")
      ? document.createElement("link")
      : document.createElement("meta");

    if (selector.includes("property=")) {
      element.setAttribute("property", selector.match(/property="([^"]+)"/)?.[1] || "");
    } else if (selector.includes("name=")) {
      element.setAttribute("name", selector.match(/name="([^"]+)"/)?.[1] || "");
    } else if (selector.startsWith("link")) {
      element.setAttribute("rel", "canonical");
    }
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
};

const canonicalUrl = (path: string) =>
  `${CLIENT_BASE_PATH}${path.endsWith("/") ? path : `${path}/`}`;

export const useBlogListSeo = () => {
  useEffect(() => {
    const title = "Hajj & Umrah Taxi Blog | Alharamain Cab";
    const description =
      "Read practical Hajj and Umrah taxi guides for Makkah, Madinah, Jeddah airport transfers, Ziyarat rides, and Saudi Arabia pilgrim transport.";
    const url = canonicalUrl("/blog");
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta(
      'meta[name="keywords"]',
      "content",
      "Hajj taxi blog, Umrah taxi guides, Makkah taxi tips, Madinah taxi tips, Jeddah airport transfer guide"
    );
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('link[rel="canonical"]', "href", url);
  }, []);
};

export const useBlogPostSeo = (post?: BlogPost | null) => {
  useEffect(() => {
    if (!post) {
      return;
    }

    const title = post.seoTitle || post.title;
    const description = post.seoDescription || post.excerpt;
    const url = post.canonicalUrl || canonicalUrl(`/blog/${post.slug}`);
    const image = post.image || `${CLIENT_BASE_PATH}/assets/img/banner.jpg`;
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="keywords"]', "content", post.tags?.join(", ") || "Umrah taxi, Hajj taxi, Saudi taxi guide");
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);
    setMeta('link[rel="canonical"]', "href", url);

    const schemaId = "blog-post-schema";
    document.getElementById(schemaId)?.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description,
      image,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      author: {
        "@type": "Organization",
        name: post.author || "Alharamain Cab",
      },
      publisher: {
        "@type": "Organization",
        name: "Alharamain Cab",
        logo: {
          "@type": "ImageObject",
          url: `${CLIENT_BASE_PATH}/assets/img/Al-Haramain-Logo-wb.svg`,
        },
      },
      mainEntityOfPage: url,
    });
    document.head.appendChild(script);
  }, [post]);
};
